
import React, { useState, useEffect } from 'react';
import { Shield } from 'lucide-react';
import { Language } from '../types';
import { TRANSLATIONS } from '../constants';

interface ResonantEnergyBalloonProps {
  onProceed?: () => void;
  language: Language;
}

const ResonantEnergyBalloon: React.FC<ResonantEnergyBalloonProps> = ({ onProceed, language }) => {
  const [isActive, setIsActive] = useState(false);
  const [charge, setCharge] = useState(0);
  const [isExpanding, setIsExpanding] = useState(false);

  const t = TRANSLATIONS[language];

  useEffect(() => {
    let interval: number;
    if (isActive && charge < 100) {
      interval = window.setInterval(() => {
        setCharge(prev => {
          if (prev >= 100) {
            clearInterval(interval);
            setIsExpanding(true);
            return 100;
          }
          return prev + 2;
        });
      }, 30);
    }
    return () => clearInterval(interval);
  }, [isActive, charge]);

  const handleActivate = () => {
    if (!isActive) {
      setIsActive(true);
    }
  };

  return (
    <div className="glass p-3 md:p-8 rounded-[1.5rem] md:rounded-[2.5rem] w-full relative border border-white/10 shadow-2xl overflow-hidden min-h-[400px] flex flex-col">
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-purple-500/40 to-transparent"></div>
      
      <div className="mb-3 md:mb-6 text-center relative z-10">
        <div className="inline-block px-2 py-0.5 rounded-full bg-purple-500/10 border border-purple-500/20 mb-2 md:mb-3">
          <span className="text-[7px] md:text-[8px] font-black text-purple-400 uppercase tracking-[0.3em] md:tracking-[0.4em]">{t.tools.shield_node}</span>
        </div>
        <h3 className="font-mono-cia text-lg md:text-2xl mb-1 text-white uppercase tracking-tighter font-black flex items-center justify-center gap-2">
          <Shield className="text-purple-400" size={24} />
          {t.tools.shield_title}
        </h3>
        <p className="text-[9px] md:text-xs text-slate-400 font-medium leading-relaxed max-w-sm mx-auto italic opacity-70">
          {t.tools.shield_subtitle}
        </p>
      </div>

      <div className="flex-grow relative flex items-center justify-center">
        {/* The Toroidal Energy Field Container */}
        <div className="relative w-64 h-64 md:w-[32rem] md:h-[32rem] flex items-center justify-center">
          {/* Toroidal Energy Field Visualization */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <svg 
              viewBox="0 0 100 100" 
              className="w-full h-full transition-all duration-1000"
              style={{ 
                filter: isActive ? `drop-shadow(0 0 ${charge / 3}px rgba(168, 85, 247, 0.8))` : 'none',
                opacity: isActive ? 0.3 + (charge / 100) * 0.7 : 0.1,
                color: charge === 100 ? '#fff' : `rgba(168, 85, 247, ${0.4 + (charge / 200)})`,
                transform: isExpanding ? 'scale(1.1)' : 'scale(1)'
              }}
            >
              {/* Toroidal Loops (Vertical) */}
              {[...Array(12)].map((_, i) => (
                <ellipse 
                  key={i}
                  cx="50" 
                  cy="50" 
                  rx={isActive ? 5 + (i * 4) + (charge / 15) : 5 + (i * 4)} 
                  ry="49" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="0.15"
                  className={isActive ? 'animate-torus-flow' : ''}
                  style={{ 
                    animationDelay: `${i * 0.15}s`,
                    transformOrigin: 'center',
                    opacity: 0.2 + (i / 20)
                  }}
                />
              ))}
              
              {/* Toroidal Loops (Horizontal) */}
              {[...Array(6)].map((_, i) => (
                <ellipse 
                  key={`h-${i}`}
                  cx="50" 
                  cy="50" 
                  rx="49" 
                  ry={isActive ? 2 + (i * 8) + (charge / 25) : 2 + (i * 8)} 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="0.1"
                  className={isActive ? 'animate-torus-flow-h' : ''}
                  style={{ 
                    animationDelay: `${i * 0.4}s`,
                    transformOrigin: 'center',
                    opacity: 0.1 + (i / 15)
                  }}
                />
              ))}

              {/* Central Core Beam */}
              {isActive && (
                <rect 
                  x="49.7" 
                  y="1" 
                  width="0.6" 
                  height="98" 
                  fill="url(#beamGradient)"
                  className="animate-pulse"
                />
              )}
              
              <defs>
                <linearGradient id="beamGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="transparent" />
                  <stop offset="50%" stopColor="currentColor" />
                  <stop offset="100%" stopColor="transparent" />
                </linearGradient>
              </defs>
            </svg>
          </div>

          {/* Status Text */}
          <div className="z-10 flex flex-col items-center">
            {!isActive ? (
              <div className="flex flex-col items-center gap-1 opacity-40">
                <span className="text-[10px] font-black uppercase tracking-widest">{t.tools.shield_standby}</span>
                <span className="text-[8px] font-mono-cia uppercase">{t.tools.shield_awaiting}</span>
              </div>
            ) : (
              <div className="flex flex-col items-center">
                <span className="text-4xl md:text-6xl font-black text-white font-mono-cia">{charge}%</span>
                <span className="text-[8px] md:text-[10px] font-black text-purple-400 uppercase tracking-[0.3em] mt-2">
                  {charge < 100 ? t.tools.shield_charging : t.tools.shield_stabilized}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="mt-6 md:mt-10 relative z-20 flex flex-col gap-3">
        {!isActive ? (
          <button 
            onClick={handleActivate}
            className="bg-purple-600 hover:bg-purple-500 text-white w-full py-4 md:py-6 rounded-2xl font-black text-xs md:text-sm uppercase tracking-[0.2em] transition-all active:scale-95 shadow-[0_10px_30px_rgba(168,85,247,0.3)] flex items-center justify-center gap-3"
          >
            <Shield size={20} />
            {t.tools.shield_button}
          </button>
        ) : charge === 100 ? (
          <button 
            onClick={onProceed}
            className="animate-fade-in bg-emerald-600 hover:bg-emerald-500 text-white w-full py-4 md:py-6 rounded-2xl font-black text-xs md:text-sm uppercase tracking-[0.2em] transition-all active:scale-95 shadow-[0_10px_30px_rgba(16,185,129,0.3)] flex flex-col items-center"
          >
            <span className="text-[10px] opacity-80 mb-1">{t.tools.shield_active}</span>
            {t.tools.shield_proceed}
          </button>
        ) : (
          <div className="w-full py-4 md:py-6 rounded-2xl bg-purple-900/20 border border-purple-500/30 flex items-center justify-center">
            <div className="flex gap-1">
              {[...Array(5)].map((_, i) => (
                <div 
                  key={i} 
                  className="w-1.5 h-1.5 bg-purple-500 rounded-full animate-bounce" 
                  style={{ animationDelay: `${i * 0.1}s` }}
                ></div>
              ))}
            </div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes ping-slow {
          0% { transform: scale(0.8); opacity: 0.8; }
          100% { transform: scale(1.5); opacity: 0; }
        }
        @keyframes spin-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes torus-flow {
          0% { stroke-dashoffset: 200; opacity: 0.2; }
          50% { opacity: 0.8; }
          100% { stroke-dashoffset: 0; opacity: 0.2; }
        }
        @keyframes torus-flow-h {
          0% { stroke-dashoffset: -200; opacity: 0.1; }
          50% { opacity: 0.4; }
          100% { stroke-dashoffset: 0; opacity: 0.1; }
        }
        .animate-ping-slow {
          animation: ping-slow 4s cubic-bezier(0, 0, 0.2, 1) infinite;
        }
        .animate-spin-slow {
          animation: spin-slow 8s linear infinite;
        }
        .animate-torus-flow {
          stroke-dasharray: 100 100;
          animation: torus-flow 4s linear infinite;
        }
        .animate-torus-flow-h {
          stroke-dasharray: 50 150;
          animation: torus-flow-h 6s linear infinite;
        }
      `}</style>
    </div>
  );
};

export default ResonantEnergyBalloon;
